import { z } from 'zod';
import { zNonEmpty, zQueryBooleanDefault, zUuid } from '@saas/shared';

/**
 * storage の DTO（03 §3.1a・02 §6）。**リードが先に固定した契約**: 追加のみ可・改名と削除は不可。
 *
 * 用語:
 *  - フォルダ（folder）: 階層を持つ入れ物。権限（ACL）はフォルダに付き、既定で親から継承する
 *  - ファイル（file）: フォルダの中の 1 行。中身は core の `files`（FileService）にあり、版ごとに 1 行ずつ増える
 *    （R2 は上書きしない・02 §7.3a）。ここでの `id` は storage のファイル行の ID で、core の `fileId` とは別
 *  - 共有リンク（share）: 社外の人にログインなしで渡すリンク。**権限の緩和ではない**（§16.8）: 社内の ACL は変わらない
 */

/** shared = 会社のフォルダ（ACL で守る） / personal = 個人フォルダ（本人だけ。knowledge には入れない・§16.7） */
export const STORAGE_FOLDER_KINDS = ['shared', 'personal'] as const;
export type StorageFolderKind = (typeof STORAGE_FOLDER_KINDS)[number];

/**
 * viewer  = 一覧・プレビュー・ダウンロード
 * editor  = viewer + 追加・新しい版・名前変更・移動・ゴミ箱へ
 * manager = editor + 権限の変更・共有リンク・ゴミ箱からの完全削除・フォルダの削除
 */
export const STORAGE_ACCESS_LEVELS = ['viewer', 'editor', 'manager'] as const;
export type StorageAccessLevel = (typeof STORAGE_ACCESS_LEVELS)[number];

export const STORAGE_GRANTEE_TYPES = ['everyone', 'department', 'role', 'user'] as const;
export type StorageGranteeType = (typeof STORAGE_GRANTEE_TYPES)[number];

export interface StorageAclEntry {
  granteeType: StorageGranteeType;
  /** everyone は null。department = departments.id / role = ロールコード（文字列）/ user = users.id */
  granteeId: string | null;
  level: StorageAccessLevel;
  /** 表示用（部署名・ロール名・氏名）。サーバが埋める */
  granteeName?: string | null;
}

/** 「読める人」の粗い集合。knowledge の 1 段目の絞り込みと、共有前の確認表示に使う（2 段目は判定器 `storage_file`） */
export interface StorageAudience {
  everyone: boolean;
  departmentIds: string[];
  roleCodes: string[];
  userIds: string[];
}

/** フォルダやファイルを業務レコードに結びつける（顧客カードのタイムラインに載せる・02 §6）。'lead'（見込み客）は 2026-10-03 に追加 */
export interface StorageSubjectLink {
  subjectType: 'customer' | 'deal' | 'project' | 'contract' | 'lead';
  subjectId: string;
  /** 表示用。サーバが引けたときだけ */
  title?: string | null;
}

export interface StorageFolderSummary {
  id: string;
  /** 更新時の競合検知に使う。更新されたら増える。 */
  revision: string;
  parentId: string | null;
  name: string;
  /** ルートからの表示用パス（'/営業/見積'）。名前変更・移動で変わる */
  path: string;
  kind: StorageFolderKind;
  /** personal の持ち主。shared は null */
  ownerId: string | null;
  /** true = 親の ACL を引き継ぐ（既定）。false = このフォルダの ACL だけで決まる */
  inheritAcl: boolean;
  /** 呼び出した人の実効レベル。読めない人には行そのものを返さない */
  myLevel: StorageAccessLevel;
  link: StorageSubjectLink | null;
  /** knowledge に入れる / 入れないの設定は knowledge 側（knowledge_folder_rules）。ここには持たない */
  childFolderCount: number;
  fileCount: number;
  createdAt: string;
  updatedAt: string;
  trashedAt: string | null;
}

export interface StorageFileSummary {
  /** storage のファイル行の ID（共有リンク・判定器 `storage_file`・knowledge の subject_id はこれ） */
  id: string;
  /** 更新時の競合検知に使う。更新されたら増える。 */
  revision: string;
  folderId: string;
  folderPath: string;
  name: string;
  mime: string;
  sizeBytes: number;
  /** 現在の版の core ファイル ID（ダウンロードは GET /storage/files/:id/url を使う。core の汎用口は判定器を通る） */
  currentFileId: string;
  version: number;
  contentHash: string | null;
  createdBy: string | null;
  createdByName: string | null;
  updatedBy: string | null;
  updatedByName: string | null;
  createdAt: string;
  updatedAt: string;
  /** 最後に誰かがダウンロード / プレビューした時刻（§16.7「直近 12 か月に誰かが開いた」） */
  lastOpenedAt: string | null;
  /** 編集中の目印（助言的ロック。wiki と同じ流儀）。無ければ null */
  lockedBy: string | null;
  lockedByName: string | null;
  lockedUntil: string | null;
  myLevel: StorageAccessLevel;
  /** 親フォルダの権限。null はファイルへの直接付与だけでアクセスしていることを示す。 */
  folderLevel: StorageAccessLevel | null;
  folderKind: 'shared' | 'personal';
  link: StorageSubjectLink | null;
  trashedAt: string | null;
  trashedBy: string | null;
  /** ゴミ箱から自動で完全削除される日（trashedAt + 30 日。legal hold 中は消えない） */
  purgeAfter: string | null;
  /** 有効な共有リンクの数（一覧の目印） */
  activeShareCount: number;
  /** 現在の版のウイルス検査結果。skipped は未検査。 */
  scanResult?: 'clean' | 'infected' | 'skipped' | null;
}

export interface StorageFileVersionSummary {
  version: number;
  fileId: string;
  sizeBytes: number;
  contentHash: string | null;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
  comment: string | null;
  isCurrent: boolean;
}

export const STORAGE_OFFICE_PREVIEW_MAX_BYTES = 8 * 1024 * 1024;
export type StorageOfficeFormat = 'docx' | 'xlsx' | 'pptx' | 'odt' | 'ods' | 'odp';
const STORAGE_OFFICE_MIME_FORMATS: Record<string, StorageOfficeFormat> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/vnd.oasis.opendocument.text': 'odt',
  'application/vnd.oasis.opendocument.spreadsheet': 'ods',
  'application/vnd.oasis.opendocument.presentation': 'odp',
};
const STORAGE_OFFICE_EXTENSIONS = new Set<StorageOfficeFormat>(['docx', 'xlsx', 'pptx', 'odt', 'ods', 'odp']);

export function storageOfficeFormat(mime: string, name: string): StorageOfficeFormat | null {
  const normalized = mime.toLowerCase();
  if (STORAGE_OFFICE_MIME_FORMATS[normalized]) return STORAGE_OFFICE_MIME_FORMATS[normalized];
  if (normalized !== 'application/octet-stream' && normalized !== 'application/zip') return null;
  const extension = name.split('.').at(-1)?.toLowerCase() as StorageOfficeFormat | undefined;
  return extension && STORAGE_OFFICE_EXTENSIONS.has(extension) ? extension : null;
}

export interface StorageOfficePreview {
  status: 'ready';
  format: StorageOfficeFormat;
  version: number;
  text: string;
  truncated: boolean;
}

export interface StorageShareSummary {
  id: string;
  targetType: 'file' | 'folder';
  targetId: string;
  targetName: string;
  /** 作成時にだけ平文の URL を返す（DB にはハッシュだけ）。一覧では null */
  url: string | null;
  hasPassword: boolean;
  expiresAt: string | null;
  maxDownloads: number | null;
  downloadCount: number;
  /** true = 受け取り専用（社外の人がこのフォルダへ上げられる。中は見えない）。targetType = folder のときだけ */
  allowUpload: boolean;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  revokedAt: string | null;
  lastAccessedAt: string | null;
}

export interface StorageUsageSummary {
  /** 全版の合計（ゴミ箱を含む。完全削除で減る） */
  usedBytes: number;
  fileCount: number;
  /** プランの上限（席数 × 1 人あたり。01 §4.1: light 5GB / standard 50GB / pro 200GB）。null = 無制限 */
  quotaBytes: number | null;
  trashedBytes: number;
  /**
   * 2026-10-03 追加（09 §11）: 内訳。usedBytes = currentBytes + previousVersionsBytes + trashedBytes。
   * reservedBytes（アップロード・コピーの途中で予約中）は usedBytes に含まない。古いサーバでは省略される
   */
  breakdown?: StorageUsageBreakdown;
}

export interface StorageUsageBreakdown {
  /** ゴミ箱でないファイルの「いま使っている版」 */
  currentBytes: number;
  /** ゴミ箱でないファイルの過去の版（版の復元は実体を共有するので二重に数えない） */
  previousVersionsBytes: number;
  previousVersionCount: number;
  /** ゴミ箱の中（ゴミ箱に入れたフォルダの配下も含む）。全版の合計 */
  trashedBytes: number;
  /** アップロード・コピーの途中で予約している容量（24 時間で解放）。core の容量管理の「予約中」と同じ値 */
  reservedBytes: number;
  reservedCount: number;
}

/** 同期クライアント・モバイルのオフライン用の変更フィード（GET /storage/changes） */
export interface StorageChange {
  /** カーソル（UUID v7）。この値より後を次回取る */
  seq: string;
  kind:
    | 'file.added'
    | 'file.updated'
    | 'file.moved'
    | 'file.trashed'
    | 'file.restored'
    | 'file.purged'
    | 'file.access_revoked'
    | 'folder.added'
    | 'folder.moved'
    | 'folder.trashed'
    | 'folder.restored'
    | 'folder.purged'
    | 'acl.changed';
  targetType: 'file' | 'folder';
  targetId: string;
  folderId: string | null;
  at: string;
}

// ---- 入力 ----

/** 名前に使えない文字（Windows と同期するので Windows の規則に合わせる）と、前後の空白・末尾のドットを弾く */
export const zStorageName = zNonEmpty
  .max(200)
  // eslint-disable-next-line no-control-regex -- 制御文字はファイル名に使えない（core の sanitizeFilename と同じ扱い）
  .refine((s) => !/[\\/:*?"<>|\u0000-\u001f]/.test(s), '名前に使えない文字が含まれています（\\ / : * ? " < > |）')
  .refine((s) => s === s.trim() && !s.endsWith('.'), '名前の前後の空白と末尾のドットは使えません')
  .refine((s) => s !== '.' && s !== '..', 'この名前は使えません');

export const zStorageAclEntry = z.object({
  granteeType: z.enum(STORAGE_GRANTEE_TYPES),
  granteeId: z.string().max(100).nullable(),
  level: z.enum(STORAGE_ACCESS_LEVELS),
});

/** 結びつけられる業務レコードの種類（'lead' = 見込み客。権限の判定は crm の判定器 `lead`） */
export const STORAGE_LINK_SUBJECT_TYPES = ['customer', 'deal', 'project', 'contract', 'lead'] as const;

export const zStorageSubjectLink = z.object({
  subjectType: z.enum(STORAGE_LINK_SUBJECT_TYPES),
  subjectId: zUuid,
});

export const zCreateStorageFolder = z.object({
  parentId: zUuid.nullable(),
  name: zStorageName,
  /** 省略時は親と同じ（ルート直下は shared）。personal の下は必ず personal */
  kind: z.enum(STORAGE_FOLDER_KINDS).optional(),
  link: zStorageSubjectLink.nullable().optional(),
});
export type CreateStorageFolderInput = z.infer<typeof zCreateStorageFolder>;

export const zUpdateStorageFolder = z.object({
  expectedRevision: z.string().regex(/^\d+$/).optional(),
  name: zStorageName.optional(),
  /** 移動先。null = ルート直下 */
  parentId: zUuid.nullable().optional(),
  link: zStorageSubjectLink.nullable().optional(),
});
export type UpdateStorageFolderInput = z.infer<typeof zUpdateStorageFolder>;

export const zSetStorageAcl = z.object({
  inheritAcl: z.boolean(),
  entries: z.array(zStorageAclEntry).max(200),
});
export type SetStorageAclInput = z.infer<typeof zSetStorageAcl>;

/** storage のファイル 1 件あたりの上限。Web・公開受け取り・同期で共通。 */
export const STORAGE_FILE_MAX_BYTES = 3 * 1024 * 1024 * 1024;

/** アップロードの開始（新規 or 既存ファイルの新しい版）。 */
export const zStartStorageUpload = z.object({
  folderId: zUuid,
  name: zStorageName,
  mime: zNonEmpty.max(150),
  /** 0 バイトのファイルも同期できるように nonnegative（Windows / macOS のフォルダ同期では珍しくない） */
  sizeBytes: z
    .number()
    .int()
    .nonnegative()
    .max(STORAGE_FILE_MAX_BYTES),
  /**
   * 同じ名前が既にあるとき: 'version' = 新しい版にする（既定）/ 'rename' = 「名前 (2).ext」で別ファイル / 'fail' = 409。
   * 同期クライアントは baseVersion を付けて 'version' を使う（サーバの版が進んでいたら 409 = 競合）
   */
  onConflict: z.enum(['version', 'rename', 'fail']).default('version'),
  baseVersion: z.number().int().positive().optional(),
  contentHash: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .optional(),
  comment: z.string().max(500).optional(),
});
export type StartStorageUploadInput = z.infer<typeof zStartStorageUpload>;

export const zCompleteStorageUpload = z.object({
  contentHash: z.string().regex(/^[0-9a-f]{64}$/, 'contentHash は SHA-256 の 16 進 64 文字'),
  /** 0 バイトのファイルも受ける（空ファイルの SHA-256 は e3b0c442… になる） */
  sizeBytes: z.number().int().nonnegative(),
});
export type CompleteStorageUploadInput = z.infer<typeof zCompleteStorageUpload>;

export const zUpdateStorageFile = z.object({
  expectedRevision: z.string().regex(/^\d+$/).optional(),
  name: zStorageName.optional(),
  folderId: zUuid.optional(),
  link: zStorageSubjectLink.nullable().optional(),
});
export type UpdateStorageFileInput = z.infer<typeof zUpdateStorageFile>;

/** Existing clients may omit the body; mobile supplies the displayed revision. */
export const zRestoreStorageVersion = z.object({
  expectedRevision: z.string().regex(/^\d+$/).optional(),
}).optional();
export type RestoreStorageVersionInput = z.infer<typeof zRestoreStorageVersion>;

/** Existing clients may still trash without an expected revision. */
export const zStorageTrashRequest = z.object({
  expectedRevision: z.string().regex(/^\d+$/).optional(),
}).optional();
export type StorageTrashRequest = z.infer<typeof zStorageTrashRequest>;

export const zCreateStorageShare = z.object({
  targetType: z.enum(['file', 'folder']),
  targetId: zUuid,
  /** 10 文字以上。省略 = パスワードなし（テナント設定で必須にできる） */
  password: z.string().min(10).max(200).optional(),
  /** 省略時は 7 日。最長 90 日（無期限は作らせない） */
  expiresInDays: z.number().int().min(1).max(90).default(7),
  maxDownloads: z.number().int().min(1).max(10000).optional(),
  allowUpload: z.boolean().default(false),
});
export type CreateStorageShareInput = z.infer<typeof zCreateStorageShare>;

export const zStorageListQuery = z.object({
  /** 省略 = ルート直下 */
  folderId: zUuid.optional(),
  /** 名前の部分一致（配下すべて。読めるフォルダだけ） */
  q: z.string().max(200).optional(),
  sort: z.enum(['name', 'updatedAt', 'size']).default('name'),
  order: z.enum(['asc', 'desc']).default('asc'),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  cursor: z.string().max(4096).optional(),
  /** Opt-in: paginate folders and files together. Omitted for legacy clients. */
  pageEntries: z.literal('true').optional(),
});
export type StorageListQuery = z.infer<typeof zStorageListQuery>;

export interface StorageListing {
  folder: StorageFolderSummary | null;
  /** ルートからこのフォルダまで（パンくず） */
  breadcrumbs: Array<{ id: string; name: string }>;
  folders: StorageFolderSummary[];
  files: StorageFileSummary[];
  nextCursor: string | null;
}

// ===========================================================================
// ここから下はサーバ実装で**追加**したもの（2026-09-20）。上の契約は一切変えていない。
// 追加のみ可の決まりに従い、既存の型・zod・定数の意味は変更していない。
// ===========================================================================

/** 判定器（SubjectPermissionRegistry）に登録する subject の種類。knowledge の 2 段目もこれを使う */
export const STORAGE_FILE_SUBJECT_TYPE = 'storage_file';
export const STORAGE_FOLDER_SUBJECT_TYPE = 'storage_folder';

/** テナント作成時に 1 つだけ作る既定のフォルダ（everyone = editor） */
export const STORAGE_DEFAULT_SHARED_FOLDER_NAME = '全社共有';
/** 各ユーザーの個人フォルダ（最初に開いたときに遅延生成） */
export const STORAGE_PERSONAL_FOLDER_NAME = 'マイファイル';

/** 01 §4.1: 1 人あたりの容量。quotaBytes = 有効ユーザー数 × これ */
export const STORAGE_BYTES_PER_USER: Record<'light' | 'standard' | 'pro', number> = {
  light: 5 * 1024 ** 3,
  standard: 50 * 1024 ** 3,
  pro: 200 * 1024 ** 3,
};

/** 課金メトリクス（日次で core.usage.record に積む） */
export const STORAGE_USAGE_METRIC = 'storage_bytes';

/** ゴミ箱に入れてから完全削除までの日数 */
export const STORAGE_TRASH_RETENTION_DAYS = 30;
/** 変更フィードの保持日数（これより古いカーソルは cursor_expired） */
export const STORAGE_CHANGES_RETENTION_DAYS = 90;
/** 助言的ロックの既定 TTL（秒）。wiki と同じく延長できる */
export const STORAGE_LOCK_TTL_SEC = 15 * 60;

/** AppError の details.reason（画面が文言を切り替えるための識別子） */
export const STORAGE_REASON = {
  quotaExceeded: 'quota_exceeded',
  nameConflict: 'name_conflict',
  versionConflict: 'version_conflict',
  uploadInProgress: 'upload_in_progress',
  cursorExpired: 'cursor_expired',
  shareExpired: 'share_expired',
  shareRevoked: 'share_revoked',
  sharePasswordRequired: 'share_password_required',
  sharePasswordLocked: 'share_password_locked',
  shareDownloadLimit: 'share_download_limit',
  shareUploadLimit: 'share_upload_limit',
  executableRejected: 'executable_rejected',
  legalHold: 'legal_hold',
  /** 2026-10-03 追加: 同期クライアントが最低対応版より古い（426） */
  clientOutdated: 'client_outdated',
  /** 2026-10-03 追加: 段階公開のフラグで止めている機能 */
  featureDisabled: 'feature_disabled',
} as const;

/**
 * サーバが受け付ける名前の規則（`zStorageName` に 2 つ足したもの）。
 *  - **NFC に正規化**してから返す（DB の一意インデックスは `lower(name)` なので、
 *    濁点の合成方法が違うだけの同名を作らせない・Windows / macOS の同期で必須）
 *  - Windows の**予約名**（CON / PRN / AUX / NUL / COM1〜9 / LPT1〜9）を弾く（同期クライアントがファイルを作れない）
 * 画面側の入力チェックは `zStorageName` のままでよい（サーバはこちらで受ける）。
 */
export const zStorageEntryName = zNonEmpty
  .max(200)
  // eslint-disable-next-line no-control-regex
  .refine((s) => !/[\\/:*?"<>|\u0000-\u001f]/.test(s), '名前に使えない文字が含まれています（\\ / : * ? " < > |）')
  .refine((s) => !s.endsWith('.'), '名前の末尾にドットは使えません')
  .refine((s) => s !== '.' && s !== '..', 'この名前は使えません')
  .refine((s) => !/^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\.|$)/i.test(s), 'Windows の予約名は使えません')
  .transform((s) => s.normalize('NFC'));

/** 共有リンク経由のアップロードでは拒否する拡張子（社内のアップロードは許可・ウイルススキャンの事業者が無いため） */
export const STORAGE_EXECUTABLE_EXTENSIONS = [
  'exe',
  'bat',
  'cmd',
  'com',
  'scr',
  'pif',
  'msi',
  'msp',
  'cpl',
  'jar',
  'js',
  'jse',
  'vbs',
  'vbe',
  'wsf',
  'wsh',
  'ps1',
  'psm1',
  'sh',
  'app',
  'dmg',
  'lnk',
  'reg',
  'hta',
] as const;

export function isExecutableFileName(name: string): boolean {
  const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase();
  return name.includes('.') && (STORAGE_EXECUTABLE_EXTENSIONS as readonly string[]).includes(ext);
}

/** アップロード開始の応答。Web v2 は multipart=true なら分割API、それ以外は uploadUrl に直接PUTして complete を呼ぶ。 */
export interface StorageUploadTicket {
  multipart?: boolean;
  fileEntryId: string;
  /** この版の core ファイル ID（complete の ?fileId= に渡すと、並行アップロードでも取り違えない） */
  fileId: string;
  version: number;
  uploadUrl: string;
  expiresAt: string;
  /** 'rename' で名前が変わったときの実際の名前 */
  name: string;
}

/** 署名付き URL の応答 */
export interface StorageSignedUrl {
  url: string;
  expiresAt: string;
  name: string;
  mime: string;
  sizeBytes: number;
}

/** GET /storage/files/:id/url */
export const zStorageUrlQuery = z.object({
  version: z.coerce.number().int().min(1).optional(),
  disposition: z.enum(['inline', 'attachment']).default('attachment'),
  ttl: z.coerce.number().int().min(60).max(3600).default(900),
});
export type StorageUrlQuery = z.infer<typeof zStorageUrlQuery>;

/** POST /storage/uploads/:id/complete の問い合わせ（どの版を確定するか。省略時は未確定の最新） */
export const zCompleteStorageUploadQuery = z.object({ fileId: zUuid.optional() });
export type CompleteStorageUploadQuery = z.infer<typeof zCompleteStorageUploadQuery>;

/** GET / PUT /storage/folders/:id/acl。継承元の分も返すので、画面は「どこから来た権限か」を出せる */
export interface StorageAclView {
  folderId: string;
  inheritAcl: boolean;
  /** このフォルダ自身に設定された ACL */
  entries: StorageAclEntry[];
  /** 継承元（親 → 祖先の順）。inheritAcl = false なら空 */
  inherited: Array<{ folderId: string; folderName: string; entries: StorageAclEntry[] }>;
  /** 呼び出した人の実効レベル */
  myLevel: StorageAccessLevel;
  /** 読める人の粗い集合（共有前の確認表示用） */
  audience: StorageAudience;
}

/** Shared-space files may grant employees, departments, or roles viewer/version-editor access. */
const zFileGrantLevel = z.enum(['viewer', 'editor']);
export const zSetStorageFileGrants = z.object({
  entries: z.array(z.union([
    z.object({ granteeType: z.enum(['user', 'department', 'role']), granteeId: z.string().min(1).max(200), level: zFileGrantLevel })
      .refine((entry) => entry.granteeType !== 'user' || zUuid.safeParse(entry.granteeId).success, '社員のIDが不正です'),
    // Existing clients can keep sending the original userId shape.
    z.object({ userId: zUuid, level: zFileGrantLevel }),
  ])).max(200),
}).refine((value) => new Set(value.entries.map((entry) => 'userId' in entry ? `user:${entry.userId}` : `${entry.granteeType}:${entry.granteeId}`)).size === value.entries.length, '同じ共有先を複数回指定できません');
export type SetStorageFileGrantsInput = z.infer<typeof zSetStorageFileGrants>;
export interface StorageFileGrantsView {
  fileEntryId: string;
  myLevel: StorageAccessLevel;
  canManage: boolean;
  entries: Array<{ granteeType: 'user' | 'department' | 'role'; granteeId: string; granteeName: string | null; level: 'viewer' | 'editor'; userId?: string; userName?: string | null }>;
}

/** ゴミ箱の 1 行 */
export interface StorageTrashEntry {
  type: 'file' | 'folder';
  id: string;
  name: string;
  /** 元の置き場所（復元先）。親が消えていれば null */
  originalPath: string;
  sizeBytes: number;
  trashedAt: string;
  trashedBy: string | null;
  trashedByName: string | null;
  purgeAfter: string | null;
  /** 現在の権限で復元できるか（ファイルは editor、フォルダは manager） */
  canRestore: boolean;
  /** 完全削除できるか（manager か storage.admin） */
  canPurge: boolean;
}

export interface StorageTrashPage {
  items: StorageTrashEntry[];
  nextCursor: string | null;
}

export const zStorageTrashPageQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(100),
  cursor: z.string().max(4096).optional(),
});
export type StorageTrashPageQuery = z.infer<typeof zStorageTrashPageQuery>;

export const zStorageTrashQuery = z.object({
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type StorageTrashQuery = z.infer<typeof zStorageTrashQuery>;

export const zStorageChangesQuery = z.object({
  cursor: zUuid.optional(),
  folderId: zUuid.optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(200),
});
export type StorageChangesQuery = z.infer<typeof zStorageChangesQuery>;

export interface StorageChangeFeed {
  changes: StorageChange[];
  /** 次に渡すカーソル。変化が無ければ前回の値がそのまま返る */
  cursor: string | null;
  /** true = まだ続きがある（すぐもう一度取る） */
  hasMore: boolean;
}

export const zStorageLinkedQuery = z.object({
  subjectType: z.enum(STORAGE_LINK_SUBJECT_TYPES),
  subjectId: zUuid,
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type StorageLinkedQuery = z.infer<typeof zStorageLinkedQuery>;

export const zStorageShareListQuery = z.object({
  targetType: z.enum(['file', 'folder']).optional(),
  targetId: zUuid.optional(),
  includeRevoked: zQueryBooleanDefault(false),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type StorageShareListQuery = z.infer<typeof zStorageShareListQuery>;

/** GET /storage/shares/:id/accesses */
export interface StorageShareAccessEntry {
  id: string;
  action: 'view' | 'unlock' | 'unlock_failed' | 'download' | 'upload';
  fileEntryId: string | null;
  fileName: string | null;
  userAgent: string | null;
  at: string;
}

// ---- 共有リンクの公開の口（ログイン不要）----

export interface StorageSharePublicFile {
  fileEntryId: string;
  name: string;
  mime: string;
  sizeBytes: number;
  updatedAt: string;
  /** 社外でも検査されていないファイルを判別できるようにする。 */
  scanResult?: 'clean' | 'infected' | 'skipped' | null;
}

/**
 * GET /public/storage/shares/:token。
 * パスワードが要るリンクでは、解錠するまで `files` も `folderName` も出さない（§16.8）
 */
export interface StorageSharePublicView {
  targetType: 'file' | 'folder';
  /** 渡した側の会社名（社外向けページの見出し。パスワードを通す前でも出す） */
  company: string;
  /** 対象の名前。パスワード必須で未解錠なら null */
  name: string | null;
  sizeBytes: number | null;
  expiresAt: string | null;
  requiresPassword: boolean;
  unlocked: boolean;
  allowUpload: boolean;
  /** 残りダウンロード回数（上限なしなら null） */
  downloadsLeft: number | null;
  /** targetType = 'folder' かつ解錠済みのときだけ */
  files: StorageSharePublicFile[] | null;
  /** 単一ファイル共有の実際のウイルス検査結果。フォルダ共有は各 files[].scanResult を使う。 */
  scanResult: 'clean' | 'infected' | 'skipped';
}

export const zUnlockStorageShare = z.object({ password: z.string().min(1).max(200) });
export type UnlockStorageShareInput = z.infer<typeof zUnlockStorageShare>;

export interface StorageShareUnlockResult {
  /** 短命のアクセストークン。以降の呼び出しは Authorization: Bearer <accessToken> で渡す */
  accessToken: string;
  expiresAt: string;
}

/** 受け取り専用リンク（allow_upload）への直接 PUT の開始 */
export const zStorageSharePublicUpload = z.object({
  name: zStorageEntryName,
  mime: zNonEmpty.max(150),
  sizeBytes: z
    .number()
    .int()
    .nonnegative()
    .max(STORAGE_FILE_MAX_BYTES),
});
export type StorageSharePublicUploadInput = z.infer<typeof zStorageSharePublicUpload>;

/** 1 つの受け取りリンクで受けられる件数と合計サイズの上限 */
export const STORAGE_SHARE_UPLOAD_MAX_FILES = 100;
export const STORAGE_SHARE_UPLOAD_MAX_BYTES = 10 * 1024 * 1024 * 1024;

// ---- 同期クライアントの端末 ----

export interface StorageSyncDeviceSummary {
  id: string;
  name: string;
  userId: string;
  userName: string | null;
  rootFolderId: string | null;
  rootFolderPath: string | null;
  lastSeenAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  /** 登録時の応答にだけ平文のトークンが入る（以降は null） */
  token: string | null;
}

export const zCreateStorageSyncDevice = z.object({
  name: zNonEmpty.max(100),
  /** 同期の起点。省略時はテナントのルート（読めるフォルダ全部） */
  rootFolderId: zUuid.nullable().optional(),
});
export type CreateStorageSyncDeviceInput = z.infer<typeof zCreateStorageSyncDevice>;

// ---- 助言的ロック（wiki と同じ「助言的」。他人がロック中でも新しい版は 409 にせず警告だけ）----

export interface StorageFileLock {
  fileEntryId: string;
  userId: string;
  userName: string | null;
  acquiredAt: string;
  expiresAt: string;
  mine: boolean;
}

export const zLockStorageFile = z.object({
  /** 他人のロックを奪う（manager か storage.admin だけ。監査に残る） */
  force: z.boolean().default(false),
});
export type LockStorageFileInput = z.infer<typeof zLockStorageFile>;

// ---- 画面の入口（GET /storage/capabilities）----

export interface StorageCapabilities {
  uploadPolicy?: { maxBytes: number; scanMaxBytes?: number; scannerReady: boolean; requiresScan: boolean; multipartThresholdBytes?: number };
  /** ファイルのレコード会話を使える（conversation 有効かつ standard / pro） */
  conversation: boolean;
  canWrite: boolean;
  canCreateShare: boolean;
  isAdmin: boolean;
  /** テナント設定（core の tenant settings の storage.* を読むだけ） */
  share: { enabled: boolean; requirePassword: boolean; maxDays: number };
  usage: StorageUsageSummary;
  /** 個人フォルダ（最初に開いたときに作られる） */
  personalFolderId: string;
  /** 2026-10-03 追加: 段階公開の機能フラグの実効値（古いサーバでは省略。画面は「無い = 使えない」と読む） */
  features?: StorageFeatureStates;
}

/** テナント設定のキー（core の tenants.settings。storage は読むだけ） */
export const STORAGE_SETTING_KEYS = {
  shareEnabled: 'storage.share.enabled',
  shareRequirePassword: 'storage.share.requirePassword',
  shareMaxDays: 'storage.share.maxDays',
} as const;
export const STORAGE_SHARE_DEFAULTS = { enabled: true, requirePassword: false, maxDays: 90 } as const;

/** 案件画面で直接使う資料フォルダ（案件の閲覧・参加権限を継承）。 */
export interface ProjectDocuments {
  root: StorageFolderSummary | null;
  canUpload: boolean;
  /** 従来の方法で紐づけたフォルダも引き続き開ける。権限は従来のまま。 */
  linkedFolders: StorageFolderSummary[];
}
export const zEnsureProjectDocuments = z.object({ name: zStorageName });

// ===========================================================================
// 2026-10-03 の追加（09 §9・§11・§12・§13・§14.1・§17）。上の契約は変えていない（追加のみ）。
// ===========================================================================

// ---- 段階公開の機能フラグ（tenants.settings.features["storage.<機能>"]）----

/**
 * 機能ごとのフラグ。値は core の機能フラグ（`core.features.isEnabled(tx, flag, defaultEnabled)`）で読む。
 * 未設定は `defaultEnabled`。個人フォルダの社内共有だけは既存の「個人フォルダは本人だけ」を変えるので既定 OFF。
 */
export const STORAGE_FEATURES = {
  activity: { flag: 'storage.activity', defaultEnabled: true, label: 'ファイルの変更履歴' },
  documentPreview: { flag: 'storage.documentPreview', defaultEnabled: true, label: 'Office 文書の PDF プレビューと PDF・Office のサムネイル' },
  asyncZip: { flag: 'storage.asyncZip', defaultEnabled: true, label: '大きい ZIP の作成' },
  personalSharing: { flag: 'storage.personalSharing', defaultEnabled: false, label: '個人フォルダの社内共有' },
  changeFeedV2: { flag: 'storage.changeFeedV2', defaultEnabled: true, label: '新しい変更フィード（同期クライアント）' },
} as const;
export type StorageFeatureName = keyof typeof STORAGE_FEATURES;
/** 実効値（フラグ AND サーバ側の前提。例: 変換ツールが無ければ documentPreview は false） */
export type StorageFeatureStates = Record<StorageFeatureName, boolean>;

/** GET /storage/admin/features（storage.admin）。切り替えは core の PATCH /admin/tenant の settings.features で行う */
export interface StorageFeatureAdminEntry {
  name: StorageFeatureName;
  flag: string;
  label: string;
  /** 会社の設定値（未設定なら既定値） */
  enabled: boolean;
  defaultEnabled: boolean;
  /** 会社が明示的に設定しているか */
  configured: boolean;
  /** サーバ側の前提を満たしているか（documentPreview は OFFICE_CONVERTER=soffice が要る） */
  available: boolean;
  unavailableReason: string | null;
}

// ---- ファイルの変更履歴（09 §9: GET /storage/files/:id/activity）----

export const STORAGE_FILE_ACTIVITY_KINDS = [
  'created',
  'version_added',
  'renamed',
  'moved',
  'trashed',
  'restored',
  'version_restored',
  'purged',
  'share_created',
  'share_revoked',
  'grants_changed',
  'personal_shares_changed',
  'lock_forced',
] as const;
export type StorageFileActivityKind = (typeof STORAGE_FILE_ACTIVITY_KINDS)[number];

/** 履歴の 1 行。公開 URL・IP・利用者エージェント・トークンは持たない。読めないフォルダの名前は出さない */
export interface StorageFileActivity {
  id: string;
  kind: StorageFileActivityKind;
  at: string;
  actorId: string | null;
  actorName: string | null;
  /** 社外の共有リンクから受け取ったファイル（操作した社員がいない） */
  viaShareLink: boolean;
  name: string | null;
  previousName: string | null;
  /** 置き場所。呼び出した人が読めないフォルダは null（名前もパスも出さない） */
  folderPath: string | null;
  previousFolderPath: string | null;
  version: number | null;
  /** 版を戻したときの元の版 */
  restoredFrom: number | null;
  /** 社内の共有先の数（grants_changed / personal_shares_changed） */
  grantCount: number | null;
  /** コピーで作られたファイル */
  copied: boolean;
}

export interface StorageFileActivityPage {
  items: StorageFileActivity[];
  nextCursor: string | null;
}

export const zStorageActivityQuery = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type StorageActivityQuery = z.infer<typeof zStorageActivityQuery>;

// ---- Office の PDF 変換・PDF / Office のサムネイル（09 §2.2・§17.6・§17.8〜17.10）----

/** LibreOffice で PDF にできる形式（旧形式の DOC / XLS / PPT と RTF も含む） */
export type StorageConvertibleFormat = StorageOfficeFormat | 'doc' | 'xls' | 'ppt' | 'rtf';
const STORAGE_CONVERTIBLE_MIME: Record<string, StorageConvertibleFormat> = {
  'application/msword': 'doc',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/rtf': 'rtf',
  'text/rtf': 'rtf',
};
const STORAGE_CONVERTIBLE_EXTENSIONS = new Set<string>(['doc', 'xls', 'ppt', 'rtf']);

export function storageConvertibleFormat(mime: string, name: string): StorageConvertibleFormat | null {
  const office = storageOfficeFormat(mime, name);
  if (office) return office;
  const normalized = mime.toLowerCase();
  if (STORAGE_CONVERTIBLE_MIME[normalized]) return STORAGE_CONVERTIBLE_MIME[normalized];
  if (normalized !== 'application/octet-stream') return null;
  const extension = name.split('.').at(-1)?.toLowerCase();
  return extension && STORAGE_CONVERTIBLE_EXTENSIONS.has(extension) ? (extension as StorageConvertibleFormat) : null;
}

/** GET /storage/files/:id/pdf-preview。準備中は retryAfterMs 後に取り直す */
export type StorageDocumentPreview =
  | { status: 'ready'; url: string; expiresAt: string; version: number }
  | { status: 'pending'; retryAfterMs: number }
  | { status: 'failed'; reason: string }
  | { status: 'unsupported'; reason: 'disabled' | 'format' | 'too_large' };

/** GET /storage/files/:id/thumbnail（画像は従来どおり。PDF・Office は変換が済むまで pending） */
export type StorageThumbnailResult = { status: 'ready'; url: string; expiresAt: string } | { status: 'pending'; retryAfterMs: number } | { status: 'unsupported' };

// ---- 大きい ZIP の非同期作成（09 §17.8 の後続）----

export const STORAGE_ZIP_JOB_MAX_ENTRIES = 5000;
export const STORAGE_ZIP_JOB_MAX_BYTES = 2 * 1024 * 1024 * 1024;
/** できた ZIP を取得できる時間 */
export const STORAGE_ZIP_JOB_TTL_HOURS = 24;

export const zCreateStorageZipJob = z
  .object({
    /** 再送をまとめる鍵（利用者ごと）。同じ鍵に別の内容は 409 */
    requestKey: zUuid,
    fileIds: z.array(zUuid).max(100).default([]),
    folderIds: z.array(zUuid).max(100).default([]),
  })
  .refine((v) => v.fileIds.length + v.folderIds.length >= 1, 'ZIP にまとめる項目を選んでください')
  .refine((v) => v.fileIds.length + v.folderIds.length <= 100, '選べる項目は 100 件までです')
  .refine((v) => new Set(v.fileIds).size === v.fileIds.length && new Set(v.folderIds).size === v.folderIds.length, '同じ項目を重ねて指定できません');
export type CreateStorageZipJobInput = z.infer<typeof zCreateStorageZipJob>;

export interface StorageZipJob {
  id: string;
  state: 'queued' | 'processing' | 'ready' | 'failed' | 'expired';
  /** 展開後のファイル・フォルダの件数（作成が進むと分かる） */
  entryCount: number | null;
  /** 原本の合計 */
  totalBytes: number | null;
  /** できた ZIP の大きさ */
  sizeBytes: number | null;
  /** 失敗の説明（利用者に見せてよい文言） */
  error: string | null;
  reason: string | null;
  createdAt: string;
  readyAt: string | null;
  /** この時刻を過ぎると取得できず、ZIP は消える */
  expiresAt: string | null;
  /** 保存するときの名前 */
  name: string;
}

export interface StorageZipJobDownload {
  url: string;
  expiresAt: string;
  name: string;
  sizeBytes: number;
}

// ---- 個人フォルダの社内共有（09 §2.2・§6.3）----

/** 個人フォルダ・ファイルを社内の指定した利用者に共有する（閲覧 / 編集）。管理（共有・社外リンク・完全削除）は持ち主だけ */
export const zSetStoragePersonalShares = z
  .object({
    entries: z.array(z.object({ userId: zUuid, level: z.enum(['viewer', 'editor']) })).max(200),
  })
  .refine((v) => new Set(v.entries.map((e) => e.userId)).size === v.entries.length, '同じ相手を 2 回指定しています');
export type SetStoragePersonalSharesInput = z.infer<typeof zSetStoragePersonalShares>;

export interface StoragePersonalShareEntry {
  userId: string;
  userName: string | null;
  level: 'viewer' | 'editor';
}

export interface StoragePersonalSharesView {
  targetType: 'file' | 'folder';
  targetId: string;
  /** 会社でこの機能が有効か */
  enabled: boolean;
  /** 呼び出した人が共有先を変えられるか（持ち主だけ） */
  canManage: boolean;
  /** 呼び出した人の実効レベル */
  myLevel: StorageAccessLevel;
  entries: StoragePersonalShareEntry[];
  /** 上位の個人フォルダから引き継いだ共有。持ち主にだけ返す */
  inherited: Array<{ folderId: string; folderName: string; entries: StoragePersonalShareEntry[] }>;
}

/**
 * `POST /storage/folders/:id/owner`（個人フォルダの引き継ぎ）の結果。
 * 以前はフォルダの要約を返そうとしていたが、管理者は他人の個人フォルダを読めないため、変更は確定したのに 404 になっていた（2026-10-03 に修正）
 */
export interface StorageFolderOwnerChange {
  id: string;
  previousOwnerId: string | null;
  ownerId: string;
  /** 持ち主を付け替えたフォルダの数（配下を含む） */
  folderCount: number;
  /** 外した社内共有（フォルダの共有先 + ファイルの共有先）の数 */
  clearedShareCount: number;
}

// ---- 新しい変更フィード（09 §12: テナント単位の順序・権限変更時の再同期要求）----

/** v2 のカーソル。`v2.<順序番号>.<権限の指紋>`（中身に意味を持たせず、そのまま返す） */
export const zStorageChangeCursorV2 = z.string().regex(/^v2\.\d{1,19}\.[0-9a-f]{16}$/, 'カーソルの形式が正しくありません');

export const zStorageChangesV2Query = z.object({
  cursor: zStorageChangeCursorV2.optional(),
  folderId: zUuid.optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(200),
});
export type StorageChangesV2Query = z.infer<typeof zStorageChangesV2Query>;

/**
 * GET /storage/v2/changes・GET /storage/sync/v2/changes。
 *  - `seq` はテナント単位でコミット順に増える番号（v1 の UUID v7 と 3 秒待ちは使わない）
 *  - `resyncRequired` = 権限の変化・カーソルの期限切れ。内容は返さない。端末は今見える一覧を取り直し、返った `cursor` から続ける
 *    （権限を失った項目はリモートの削除と区別し、手元の未送信の変更を勝手に消さない）
 *  - 初回は先に GET …/v2/changes/cursor でカーソルを取り、その後で一覧を取る（その間の変更は重ねて届くが冪等に処理できる）
 */
export interface StorageChangeFeedV2 {
  version: 2;
  changes: StorageChange[];
  cursor: string;
  hasMore: boolean;
  resyncRequired: boolean;
  reason: 'permission_changed' | 'subject_changed' | 'cursor_expired' | null;
}

export interface StorageChangeCursorV2 {
  version: 2;
  cursor: string;
}

// ---- 同期クライアントの配布と最低対応版（09 §13・§14.1）----

export const STORAGE_SYNC_PLATFORMS = ['win', 'mac'] as const;
export type StorageSyncPlatform = (typeof STORAGE_SYNC_PLATFORMS)[number];
export const zStorageSyncReleaseQuery = z.object({ platform: z.enum(STORAGE_SYNC_PLATFORMS) });

/** GET /storage/sync-client/release?platform=win|mac（ログイン不要。サーバの環境変数で設定） */
export interface StorageSyncClientRelease {
  platform: StorageSyncPlatform;
  /** 配布中の最新版（未設定は null） */
  version: string | null;
  /** これより古いクライアントは書き込み（アップロード・改名・移動・削除）を 426 で断る */
  minSupportedVersion: string | null;
  url: string | null;
  sha256: string | null;
  notes: string | null;
  configured: boolean;
}

/** 同期クライアントが毎回送るヘッダ（小文字。Node の受信ヘッダは小文字になる） */
export const STORAGE_SYNC_VERSION_HEADER = 'x-storage-sync-version';
export const STORAGE_SYNC_PLATFORM_HEADER = 'x-storage-sync-platform';
